/**
 * Conference session detail page.
 * Route: `/conferences/sessions/:sessionId/history`.
 *
 * Two-column: participants on the left, event timeline on the right.
 * The timeline is colour-coded by event kind so a moderator can scan
 * for the moment something interesting happened (joins green, leaves
 * grey, mutes amber, hand_raised cyan, recording red, kicks/end rose).
 *
 * Calmer companion to the live dashboard — no pulsing dots, no realtime
 * subscriptions, just static history.
 */
import { useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Mic, MicOff, Hand, PhoneIncoming, PhoneOff,
  Lock, Unlock, UserX, Radio, Volume2, Sparkles, Activity,
  ShieldCheck, ShieldOff,
} from 'lucide-react';
import {
  conferenceHistory,
  type EventEntry,
  type ParticipantHistory,
} from '@/api/conferenceHistory';
import { conferenceRooms } from '@/api/conferenceRooms';
import { PageHeader } from '@/components/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

function formatStartedAt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit', second: '2-digit',
      });
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleTimeString(undefined, {
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      });
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return `${h}h ${rm}m`;
}

// Per-event-kind icon + colour palette. Falls back to neutral.
const KIND_VISUALS: Record<string, { Icon: typeof Mic; tone: string; label: string }> = {
  participant_joined:   { Icon: PhoneIncoming, tone: 'text-emerald-300 border-emerald-700/60 bg-emerald-950/30', label: 'Joined' },
  participant_left:     { Icon: PhoneOff,      tone: 'text-slate-400 border-slate-700 bg-slate-900/40',         label: 'Left' },
  participant_muted:    { Icon: MicOff,        tone: 'text-amber-300 border-amber-700/60 bg-amber-950/30',      label: 'Muted' },
  participant_unmuted:  { Icon: Mic,           tone: 'text-amber-200 border-amber-700/40 bg-amber-950/20',      label: 'Unmuted' },
  hand_raised:          { Icon: Hand,          tone: 'text-cyan-300 border-cyan-700/60 bg-cyan-950/30',         label: 'Hand raised' },
  hand_lowered:         { Icon: Hand,          tone: 'text-cyan-200 border-cyan-700/30 bg-cyan-950/15',         label: 'Hand lowered' },
  speaker_opened:       { Icon: Volume2,       tone: 'text-violet-300 border-violet-700/60 bg-violet-950/30',   label: 'Speaker opened' },
  speaker_closed:       { Icon: Volume2,       tone: 'text-violet-300 border-violet-700/30 bg-violet-950/15',   label: 'Speaker closed' },
  recording_started:    { Icon: Radio,         tone: 'text-rose-300 border-rose-700/60 bg-rose-950/30',         label: 'Recording started' },
  recording_stopped:    { Icon: Radio,         tone: 'text-rose-200 border-rose-700/30 bg-rose-950/15',         label: 'Recording stopped' },
  lock_changed_locked:  { Icon: Lock,          tone: 'text-orange-300 border-orange-700/60 bg-orange-950/30',   label: 'Locked' },
  lock_changed_unlocked:{ Icon: Unlock,        tone: 'text-orange-200 border-orange-700/30 bg-orange-950/15',   label: 'Unlocked' },
  conference_ended:     { Icon: PhoneOff,      tone: 'text-rose-300 border-rose-700/60 bg-rose-950/40',         label: 'Conference ended' },
  kick:                 { Icon: UserX,         tone: 'text-rose-300 border-rose-700/60 bg-rose-950/30',         label: 'Kicked' },
};

function visualsFor(kind: string): { Icon: typeof Mic; tone: string; label: string } {
  return KIND_VISUALS[kind] ?? {
    Icon: Sparkles,
    tone: 'text-slate-300 border-slate-700 bg-slate-900/30',
    label: kind.replace(/_/g, ' '),
  };
}

export function ConferenceSessionDetailPage() {
  const { sessionId = '' } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['conferenceSessionDetail', sessionId],
    queryFn: () => conferenceHistory.detail(sessionId),
    enabled: !!sessionId,
    retry: false,
  });

  const { data: room } = useQuery({
    queryKey: ['conferenceRoom', data?.room_id],
    queryFn: () => conferenceRooms.get(data!.room_id),
    enabled: !!data?.room_id,
    retry: false,
  });

  const participants: ParticipantHistory[] = useMemo(
    () => data?.participants ?? [],
    [data],
  );
  const events: EventEntry[] = useMemo(() => data?.events ?? [], [data]);

  if (isLoading) {
    return <div className="text-sm text-slate-400">Loading session detail…</div>;
  }
  if (error) {
    return (
      <div className="rounded-lg border border-rose-700/60 bg-rose-950/30 px-4 py-3 text-sm text-rose-300">
        Failed to load session: {error instanceof Error ? error.message : 'unknown error'}.
      </div>
    );
  }
  if (!data) {
    return <div className="text-sm text-slate-400">Session not found.</div>;
  }

  const backRoomId = data.room_id;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={room ? `Conference · ${room.name}` : 'Conference'}
        title={`Session ${data.id.slice(0, 8)}`}
        description="Full timeline of a single conference session: every participant and every event in order."
        actions={(
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/conferences/${backRoomId}/history`)}
          >
            <ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Back to room history
          </Button>
        )}
      />

      {/* Session metadata card */}
      <div className="grid grid-cols-2 gap-4 rounded-lg border border-border bg-card p-4 shadow-sm sm:grid-cols-4">
        <Metric label="Started" value={formatStartedAt(data.started_at)} />
        <Metric label="Ended" value={data.ended_at ? formatStartedAt(data.ended_at) : 'Live'} />
        <Metric label="Duration" value={formatDuration(data.duration_seconds)} />
        <Metric
          label="Moderator"
          value={
            data.had_moderator ? (
              <Badge variant="outline" className="border-sky-700/60 text-sky-300">
                <ShieldCheck className="mr-1 h-3 w-3" /> Yes
              </Badge>
            ) : (
              <Badge variant="outline" className="border-slate-700 text-slate-400">
                <ShieldOff className="mr-1 h-3 w-3" /> No
              </Badge>
            )
          }
        />
        <Metric label="Total joins" value={String(data.participant_count)} />
        <Metric label="Unique callers" value={String(data.unique_caller_count)} />
        <Metric
          label="Customer recording"
          value={
            data.recording_customer_path ? (
              <span className="inline-flex items-center gap-1 text-rose-400">
                <Mic className="h-3 w-3" /> Saved
              </span>
            ) : <span className="text-slate-500">None</span>
          }
        />
        <Metric label="Ended reason" value={data.ended_reason ?? '—'} />
      </div>

      {/* Recording panel — surfaces the customer-facing recording path
          captured by the finalize pipeline. There's no /api/conferences
          download endpoint yet (see backlog: a customer-scoped streaming
          route would let us drop an inline <AudioPlayer>), so for now we
          display the path so the moderator knows the bytes exist and
          can request export through support. */}
      {data.recording_customer_path && (
        <RecordingPanel path={data.recording_customer_path} />
      )}

      {/* Two-column body */}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
        {/* Participants */}
        <section className="space-y-3">
          <h2 className="font-mono text-xs uppercase tracking-widest text-slate-400">
            Participants
          </h2>
          <div className="overflow-hidden rounded-lg border border-border bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-secondary/40 text-xs uppercase tracking-wide text-slate-400">
                  <th className="px-3 py-2 text-left">Caller</th>
                  <th className="px-3 py-2 text-left">Role</th>
                  <th className="px-3 py-2 text-left">Joined</th>
                  <th className="px-3 py-2 text-left">Left</th>
                  <th className="px-3 py-2 text-right">Hands</th>
                </tr>
              </thead>
              <tbody>
                {participants.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-xs text-slate-500">No participants recorded.</td></tr>
                )}
                {participants.map((p) => (
                  <tr key={p.id} className="border-t border-border/70">
                    <td className="px-3 py-2 font-mono text-xs text-slate-300">
                      {p.caller_id_number ?? <span className="text-slate-500">unknown</span>}
                    </td>
                    <td className="px-3 py-2">
                      {p.is_host ? (
                        <Badge variant="outline" className="border-sky-700/60 text-sky-300">
                          <ShieldCheck className="mr-1 h-3 w-3" /> Moderator
                        </Badge>
                      ) : (
                        <span className="text-xs text-slate-400">Listener</span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-300">{formatTime(p.joined_at)}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-400">
                      {p.left_at ? formatTime(p.left_at) : '—'}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-300">
                      {p.raised_hand_count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Event timeline */}
        <section className="space-y-3">
          <h2 className="font-mono text-xs uppercase tracking-widest text-slate-400">
            Event timeline
          </h2>
          <div className="rounded-lg border border-border bg-card p-4">
            {events.length === 0 ? (
              <p className="py-6 text-center text-xs text-slate-500">No events recorded for this session.</p>
            ) : (
              <ol className="relative ml-2 space-y-3 border-l border-border pl-5">
                {events.map((e) => {
                  const v = visualsFor(e.kind);
                  const subject = e.display_name ?? e.e164 ?? e.member_id;
                  return (
                    <li key={e.id} className="relative">
                      <span className="absolute -left-[26px] flex h-5 w-5 items-center justify-center rounded-full border border-border bg-background">
                        <v.Icon className="h-3 w-3 text-slate-300" />
                      </span>
                      <div className={`flex items-start justify-between gap-3 rounded-md border px-3 py-2 ${v.tone}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 text-xs font-medium">
                            <span>{v.label}</span>
                            {subject && (
                              <span className="font-mono text-[11px] opacity-80">· {subject}</span>
                            )}
                          </div>
                          {e.details && Object.keys(e.details).length > 0 && (
                            <div className="mt-1 font-mono text-[10px] opacity-70">
                              {Object.entries(e.details).map(([k, val]) => (
                                <span key={k} className="mr-2">
                                  {k}={typeof val === 'string' ? val : JSON.stringify(val)}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <time className="shrink-0 font-mono text-[10px] uppercase tracking-wider opacity-70">
                          {formatTime(e.occurred_at)}
                        </time>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
          <p className="text-[11px] text-slate-500">
            <Activity className="mr-1 inline h-3 w-3" />
            Showing {events.length} events in chronological order.
          </p>
        </section>
      </div>

      <p className="text-xs text-slate-500">
        Looking for a different session?{' '}
        <Link to={`/conferences/${backRoomId}/history`} className="text-sky-400 hover:underline">
          Browse all sessions for this room
        </Link>
        .
      </p>
    </div>
  );
}

function RecordingPanel({ path }: { path: string }) {
  // We expose the storage path so the moderator can see that the
  // recording exists, and (when it's an https:// URL) a direct link to
  // open it. Local FS paths (`/var/ivrello/...`) and S3 keys without an
  // https prefix don't render as clickable — they need a backend
  // streaming/download route, which is a planned follow-up.
  const isHttpUrl = path.startsWith('https://') || path.startsWith('http://');

  return (
    <section className="space-y-3">
      <h2 className="font-mono text-xs uppercase tracking-widest text-slate-400">
        Customer recording
      </h2>
      <div className="rounded-lg border border-rose-700/40 bg-rose-950/20 p-4">
        <div className="flex flex-wrap items-start gap-3">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-rose-900/40 text-rose-300">
            <Mic className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1 space-y-1">
            <p className="text-sm font-medium text-rose-100">
              Recording captured
            </p>
            <p className="break-all font-mono text-[11px] text-rose-200/80">
              {path}
            </p>
            {isHttpUrl ? (
              <p className="pt-1 text-xs text-rose-200/90">
                <a
                  href={path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline decoration-rose-400/60 underline-offset-2 hover:decoration-rose-300"
                >
                  Open recording in a new tab
                </a>
              </p>
            ) : (
              <p className="pt-1 text-xs text-rose-200/70">
                Recording is on platform storage. Contact support to export
                the file &mdash; an in-portal download route is planned.
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Metric({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
      <span className="text-sm text-slate-200">{value}</span>
    </div>
  );
}
